import React, { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MaskReveal, Reveal } from "@/components/motion/Reveal";
import { IMAGES } from "@/lib/media";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EVENT_TYPES = ["Corporate Event", "Club / Festival", "Luxury Private Party", "Product Launch / Brand Activation", "Other"];

// Moet exact overeenkomen met de pakketnamen in het CRM (workspace 'daniq'),
// anders kan er geen automatische offerte worden aangemaakt.
const LINE_UPS = ["DJ", "DJ + live percussie", "DJ + percussie + MC"];

// Publieke intake van het CRM: maakt boeking + lead aan, en stuurt bij een
// bekend pakket automatisch een offerte met ondertekenlink.
const CRM_INTAKE_URL =
  process.env.REACT_APP_CRM_INTAKE_URL ||
  "https://pdqdrdddgbiiktcwdslv.supabase.co/functions/v1/photobooth-intake";

const Field = ({ label, children }) => (
  <label className="flex flex-col gap-2">
    <span className="text-[11px] uppercase tracking-[0.25em] text-white/50">{label}</span>
    {children}
  </label>
);

const inputCls =
  "w-full rounded-xl bg-white/5 border border-white/10 px-4 py-3 text-white placeholder:text-white/30 outline-none focus:border-magenta focus:bg-white/[0.07] transition-colors duration-300";

export const Booking = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", event_date: "", event_type: "", line_up: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.event_type) {
      toast.error("Please fill in name, email, phone and event type.");
      return;
    }
    setLoading(true);
    try {
      // Het CRM is de bestemming: boeking + lead, en bij een bekend pakket
      // meteen een offerte met ondertekenlink. Dit gaat EERST, zodat de
      // aanvraag ook binnenkomt als de eigen backend niet draait (zoals op
      // de preview-omgeving, die alleen de statische site serveert).
      if (form.event_date) {
        const resp = await fetch(CRM_INTAKE_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            workspace: "daniq",
            customer_name: form.name,
            email: form.email,
            phone: form.phone,
            event_date: form.event_date,
            package: form.line_up || undefined,
            message: [form.event_type && `Type event: ${form.event_type}`, form.message]
              .filter(Boolean)
              .join("\n"),
          }),
        });
        if (!resp.ok) {
          const body = await resp.json().catch(() => ({}));
          throw new Error(body.error || `CRM gaf ${resp.status}`);
        }
      }

      // De eigen backend is optioneel: hij bewaart een kopie, maar draait
      // niet op de preview. Faalt hij, dan mag dat de aanvraag niet blokkeren.
      try {
        await axios.post(`${API}/bookings`, form);
      } catch (backendErr) {
        console.warn("Eigen backend niet bereikbaar; aanvraag staat in het CRM.", backendErr);
      }

      setDone(true);
      toast.success("Inquiry sent — DaniQ's team will be in touch.");
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="booking" className="relative py-28 lg:py-40 px-6 lg:px-16 overflow-hidden">
      <div className="pointer-events-none absolute inset-0 opacity-30">
        <img src={IMAGES.ctaTexture} alt="" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-ink-950/70" />
      </div>

      <div className="relative max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-14">
        <div className="lg:col-span-5">
          <Reveal><span className="text-xs uppercase tracking-[0.4em] text-gold">Booking</span></Reveal>
          <h2 className="mt-4 font-display text-6xl sm:text-7xl lg:text-8xl leading-[0.82] tracking-wide text-white">
            <MaskReveal>LET'S MAKE</MaskReveal>
            <MaskReveal delay={0.1}>IT <span className="text-magenta">LOUD</span></MaskReveal>
          </h2>
          <p className="mt-6 max-w-md text-white/60 leading-relaxed">
            Tell us about your event and DaniQ's team will craft the perfect set. Clubs, festivals, luxury private and brand activations worldwide.
          </p>
          <div className="mt-10 flex flex-col gap-3">
            <a href="mailto:bookings@daniq.live" data-testid="booking-email-link" className="group flex items-center gap-3 text-white/80 hover:text-magenta transition-colors">
              <span className="font-display text-2xl tracking-[0.15em]">bookings@daniq.live</span>
            </a>
            <div className="flex gap-3 mt-2">
              {["Agency", "Press Kit", "Rider"].map((t) => (
                <span key={t} className="rounded-full border border-white/15 px-4 py-2 text-xs uppercase tracking-[0.15em] text-white/60">{t}</span>
              ))}
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          <div className="glass rounded-3xl p-8 lg:p-10 relative overflow-hidden">
            <AnimatePresence mode="wait">
              {done ? (
                <motion.div
                  key="success"
                  data-testid="booking-success"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="flex flex-col items-center justify-center text-center py-16"
                >
                  <motion.div
                    initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 200, delay: 0.1 }}
                    className="flex h-20 w-20 items-center justify-center rounded-full bg-magenta glow-magenta"
                  >
                    <Check className="h-10 w-10 text-white" />
                  </motion.div>
                  <h3 className="mt-6 font-display text-4xl tracking-[0.15em] text-white">INQUIRY RECEIVED</h3>
                  <p className="mt-3 max-w-sm text-white/60">Thank you, {form.name.split(" ")[0] || "there"}. DaniQ's team will reach out shortly to lock in the details.</p>
                  <button
                    data-testid="booking-reset"
                    onClick={() => { setDone(false); setForm({ name: "", email: "", phone: "", event_date: "", event_type: "", message: "" }); }}
                    className="mt-8 rounded-full border border-white/20 px-6 py-3 text-xs uppercase tracking-[0.2em] text-white/80 hover:border-white transition-colors"
                  >
                    Send another
                  </button>
                </motion.div>
              ) : (
                <motion.form
                  key="form"
                  data-testid="booking-form"
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                  onSubmit={submit}
                  className="flex flex-col gap-5"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <Field label="Name *">
                      <input data-testid="booking-name" className={inputCls} value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Your name" />
                    </Field>
                    <Field label="Email *">
                      <input data-testid="booking-email" type="email" className={inputCls} value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@company.com" />
                    </Field>
                    <Field label="Phone *">
                      <input data-testid="booking-phone" className={inputCls} value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+31 ..." />
                    </Field>
                    <Field label="Event Date">
                      <input data-testid="booking-date" type="date" className={`${inputCls} [color-scheme:dark]`} value={form.event_date} onChange={(e) => set("event_date", e.target.value)} />
                    </Field>
                  </div>

                  <Field label="Event Type *">
                    <Select value={form.event_type} onValueChange={(v) => set("event_type", v)}>
                      <SelectTrigger data-testid="booking-event-type" className={`${inputCls} h-auto`}>
                        <SelectValue placeholder="Select event type" />
                      </SelectTrigger>
                      <SelectContent className="bg-ink-800 border-white/10 text-white">
                        {EVENT_TYPES.map((t) => (
                          <SelectItem key={t} value={t} data-testid={`event-type-${t}`} className="focus:bg-magenta/20 focus:text-white">{t}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>

                  <Field label="Line-up">
                    <Select value={form.line_up} onValueChange={(v) => set("line_up", v)}>
                      <SelectTrigger data-testid="booking-line-up" className={`${inputCls} h-auto`}>
                        <SelectValue placeholder="DJ, or DJ with live percussion / MC" />
                      </SelectTrigger>
                      <SelectContent className="bg-ink-800 border-white/10 text-white">
                        {LINE_UPS.map((t) => (
                          <SelectItem key={t} value={t} data-testid={`line-up-${t}`} className="focus:bg-magenta/20 focus:text-white">{t}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>

                  <Field label="Message">
                    <textarea data-testid="booking-message" rows={4} className={`${inputCls} resize-none`} value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="Venue, capacity, budget, vibe..." />
                  </Field>

                  <button
                    data-testid="booking-submit"
                    type="submit"
                    disabled={loading}
                    className="group relative mt-2 flex items-center justify-center gap-3 overflow-hidden rounded-full bg-magenta px-8 py-4 text-sm font-medium uppercase tracking-[0.2em] text-white glow-magenta transition-transform duration-300 hover:scale-[1.02] disabled:opacity-60"
                  >
                    {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-4 w-4" />}
                    {loading ? "Sending" : "Send Inquiry"}
                  </button>
                </motion.form>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};
